import { useCallback, useEffect, useRef, useState } from 'react'
import { FixedSizeList, ListChildComponentProps } from 'react-window'

import { PageThumbnailItem } from './PageThumbnailItem'
import { ResizeHandle, SidebarInner } from './pageThumbnailSidebarStyles'
import { useThumbnailSidebarOpenTransition } from './useThumbnailSidebarOpenTransition'

const ITEM_HEIGHT = 160
const DEFAULT_WIDTH_PX = 112
const MIN_WIDTH_PX = 56
const MAX_WIDTH_PX = 224

interface PageThumbnailSidebarProps {
  totalPages: number
  currentPage: number
  isOpen: boolean
  sidebarWidth: number | null
  pdfUrl?: string
  onPageSelect: (pageNumber: number) => void
  onWidthChange: (width: number) => void
  onResizeEnd: () => void
  handleResetZoom: () => void
}

interface RowData {
  currentPage: number
  totalPages: number
  pdfUrl: string | undefined
  onPageSelect: (pageNumber: number) => void
}

function ThumbnailRow({
  index,
  style,
  data,
}: ListChildComponentProps<RowData>) {
  const pageNumber = index + 1
  const { currentPage, totalPages, pdfUrl, onPageSelect } = data

  return (
    <div style={{ ...style, overflow: 'hidden' }}>
      <PageThumbnailItem
        pageNumber={pageNumber}
        isActive={pageNumber === currentPage}
        onClick={onPageSelect}
        pdfUrl={pdfUrl}
        allowPdfFallback={pageNumber === currentPage}
        totalPages={totalPages}
      />
    </div>
  )
}

export function PageThumbnailSidebar({
  totalPages,
  currentPage,
  isOpen,
  sidebarWidth,
  pdfUrl,
  onPageSelect,
  onWidthChange,
  onResizeEnd,
  handleResetZoom,
}: PageThumbnailSidebarProps) {
  const listRef = useRef<FixedSizeList<RowData>>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerHeight, setContainerHeight] = useState(0)
  const openLayoutSettled = useThumbnailSidebarOpenTransition(
    isOpen,
    handleResetZoom
  )
  const prevPageRef = useRef(currentPage)
  const prevListReadyRef = useRef(false)
  const isResizingRef = useRef(false)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0]
      if (entry) {
        setContainerHeight(entry.contentRect.height)
      }
    })

    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  const effectiveWidth = sidebarWidth ?? DEFAULT_WIDTH_PX
  const itemHeight = Math.round(
    ITEM_HEIGHT * (effectiveWidth / DEFAULT_WIDTH_PX)
  )

  const listReady =
    isOpen &&
    openLayoutSettled &&
    containerHeight > 0 &&
    totalPages >= currentPage &&
    currentPage >= 1

  useEffect(() => {
    const pageChanged = currentPage !== prevPageRef.current
    prevPageRef.current = currentPage

    const becameReady = listReady && !prevListReadyRef.current
    prevListReadyRef.current = listReady

    if (!listReady) return
    if (!pageChanged && !becameReady) return

    const scrollToActive = () => {
      listRef.current?.scrollToItem(currentPage - 1, 'center')
    }

    scrollToActive()
    const rafId = window.requestAnimationFrame(scrollToActive)

    return () => {
      window.cancelAnimationFrame(rafId)
    }
  }, [currentPage, listReady])

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizingRef.current) return
      const delta = e.clientX - startXRef.current
      const clamped = Math.min(
        MAX_WIDTH_PX,
        Math.max(MIN_WIDTH_PX, startWidthRef.current + delta)
      )
      onWidthChange(clamped)
    },
    [onWidthChange]
  )

  const handleResizeEnd = useCallback(() => {
    if (!isResizingRef.current) return
    isResizingRef.current = false
    document.body.style.cursor = ''
    document.body.style.userSelect = ''
    onResizeEnd()
  }, [onResizeEnd])

  useEffect(() => {
    document.addEventListener('mousemove', handleResizeMove)
    document.addEventListener('mouseup', handleResizeEnd)
    return () => {
      document.removeEventListener('mousemove', handleResizeMove)
      document.removeEventListener('mouseup', handleResizeEnd)
    }
  }, [handleResizeMove, handleResizeEnd])

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      isResizingRef.current = true
      startXRef.current = e.clientX
      startWidthRef.current = sidebarWidth ?? DEFAULT_WIDTH_PX
      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'
    },
    [sidebarWidth]
  )

  const handlePageSelect = useCallback(
    (pageNumber: number) => {
      onPageSelect(pageNumber)
    },
    [onPageSelect]
  )

  const itemData: RowData = {
    currentPage,
    totalPages,
    pdfUrl,
    onPageSelect: handlePageSelect,
  }

  const initialScrollOffset = Math.max(0, (currentPage - 1) * itemHeight)

  return (
    <SidebarInner ref={containerRef}>
      {listReady && (
        <FixedSizeList<RowData>
          key={`${totalPages}-${itemHeight}`}
          ref={listRef}
          height={containerHeight}
          width='100%'
          itemCount={totalPages}
          itemSize={itemHeight}
          itemData={itemData}
          initialScrollOffset={initialScrollOffset}
          overscanCount={3}>
          {ThumbnailRow}
        </FixedSizeList>
      )}
      {isOpen && <ResizeHandle onMouseDown={handleResizeStart} />}
    </SidebarInner>
  )
}
